import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";
import {
  AddQuesSkeleton,
  AddTaskSkeleton,
  random32BitBinary,
  subNumCountingCapital,
} from "../../utils/constants";
import XmotTask from "./XmotTask";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import XmotQuestion from "./XmotQuestion";
import LogicDialog from "./logicDialog/LogicDialog";
import ConfirmationModal from "../../components/sharedComponents/ConfirmationModal";
import { showToast } from "../../services/toast/toastService";
import {
  checkIsSameBlock,
  getRandomizeBlockItems,
  getRandomizeSelectedBlockItems,
  isEmptyCheck,
} from "../../utils/commonLogics";
import XmotRandomizationBlock from "./XmotRandomizationBlock";
import ShowTQItemHeading from "./xmotTQSharedComponents/ShowTQItemHeading";

const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_THUMB_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png"];
const PREFERRED_WIDTH = 2560;
const PREFERRED_HEIGHT = 1440;
const VIDEO_MAX_SIZE = 100 * 1024 * 1024; // 100MB
const VIDEO_ALLOWED_TYPE = "video/mp4";

const XmotTaskQuestion = ({
  taskQuestion,
  setTaskquestion,
  onImageUpload,
  onVideoUpload,
  onDeleteTaskQuestion,
  deleteTQLoading = false,
  onDeleteSkipLogic,
  isLocked = false,
  surveyType = "",
  onDeleteTaskQuestionBlock,
}) => {
  const [isSLDialogVisible, setIsSLDialogVisible] = useState(false);
  const [selectedTQIndex, setSelectedTQIndex] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [deleteAllconfirmModalVisible, setDeleteAllconfirmModalVisible] =
    useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    index: null,
    deleteType: "",
    nestedDelete: false,
    parentIndex: 0,
    errorMsg: "",
  });
  const taskQuesRefs = useRef([]);
  const scrollToIndex = useRef(null);

  // Making grouped taskQuestion to handle Randomization block

  const groupedTaskQuestions = useMemo(() => {
    const results = [];
    let tempGroup = null;
    if (taskQuestion && taskQuestion?.length) {
      taskQuestion.forEach((item, idx) => {
        if (
          (!item?.bId && !item?.block_id) ||
          item?.type === "randomization_block"
        ) {
          results.push({ itemIndex: idx, ...item });
          tempGroup = null;
          return;
        }

        if (
          !tempGroup ||
          (tempGroup?.block_id !== 0
            ? tempGroup?.block_id !== item?.block_id
            : tempGroup?.bId !== item?.bId)
        ) {
          tempGroup = {
            block_id: item?.block_id,
            bId: item?.bId,
            block_name: item?.block_name,
            type: "randomization_block",
            itemIndex: idx,
            blockTaskQues: [item],
          };
          results.push(tempGroup);
        } else {
          tempGroup?.blockTaskQues.push(item);
        }
      });
    }

    return results;
  }, [taskQuestion]);

  const getRandomizeBlocks = useMemo(() => {
    return getRandomizeBlockItems(taskQuestion);
  }, [taskQuestion]);

  // console.log("taskQuestion", taskQuestion);
  // console.log("groupedTaskQuestions", groupedTaskQuestions);

  const cloneTaskQuestions = () => {
    let getSelectedItems = taskQuestion.filter((data) => data.isSelected);
    const getSelectedIndexes = taskQuestion
      .map((data, index) => (data.isSelected === true ? index : -1))
      .filter((index) => index !== -1);
    const lastSelectedIndex = getSelectedIndexes[getSelectedIndexes.length - 1];

    if (getSelectedItems?.length > 0) {
      getSelectedItems = getSelectedItems.map((selectedItem) => {
        return getCloneItemData(selectedItem, false, {});
      });
    }

    let newUpdatedTasks = [
      ...taskQuestion.slice(0, lastSelectedIndex + 1),
      ...getSelectedItems,
      ...taskQuestion.slice(lastSelectedIndex + 1),
    ];
    newUpdatedTasks = newUpdatedTasks.map((data) => {
      return { ...data, isSelected: false };
    });
    setTaskquestion(newUpdatedTasks);

    scrollToIndex.current = lastSelectedIndex + 1;
  };

  // Scroll to the inserted index after render
  useLayoutEffect(() => {
    const index = scrollToIndex.current;
    if (index != null && taskQuesRefs.current[index]) {
      taskQuesRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      scrollToIndex.current = null; // clear after scrolling
    }
  }, [taskQuestion]);

  const getCloneItemData = (
    getSelectedItemData,
    extractedClone = false,
    extractedCloneData = {},
  ) => {
    let getSelectedItem = { ...getSelectedItemData };
    if (getSelectedItem?.type === "question") {
      const selectedQuestionOption = getSelectedItem?.selectedQuestionOption;
      const selectedTargetQuesType = extractedCloneData?.selectedTargetQuesType;
      getSelectedItem = {
        ...getSelectedItem,
        // questionName: `${getSelectedItem.questionName}- clone`,
        skipLogic: [],
        question_id: 0,
        tId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
        qId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
        task_id: 0,
        ...(extractedClone && {
          questionName: "",
          extractedQId:
            getSelectedItemData?.question_id || getSelectedItemData?.qId,
          extractedTId:
            getSelectedItemData?.task_id || getSelectedItemData?.tId,
          isExtraction: true,
          extracted_id: 0,
          selectedQuestionOption: extractedCloneData?.selectedTargetQuesType,
          selectedExtractedAnsOpt:
            extractedCloneData?.selectedExtractedAnsOpt || "",
          selectedTargetQuesType:
            extractedCloneData?.selectedTargetQuesType || "",
          [selectedTargetQuesType]: {
            ...getSelectedItemData[selectedTargetQuesType],
            options: getSelectedItemData?.[selectedQuestionOption]?.options,
          },
        }),
      };
    } else {
      let updatedArray = [];
      if (
        getSelectedItem?.subTaskQues &&
        getSelectedItem?.subTaskQues?.length > 0
      ) {
        updatedArray = getSelectedItem?.subTaskQues.map((item) => ({
          ...item,
          // questionName: `${getSelectedItem.questionName}- clone`,
          skipLogic: [],
          question_id: 0,
          questionName: `${item?.questionName || ""} - Clone`,
          qId: `${random32BitBinary()}_${getSelectedItem?.subTaskQues.length + 1}`,
          tId: `${random32BitBinary()}_${getSelectedItem?.subTaskQues.length + 1}`,
          task_id: 0,
        }));
      }
      getSelectedItem = {
        ...getSelectedItem,
        // taskName: `${getSelectedItem.taskName}- clone`,
        skipLogic: [],
        subTaskQues: updatedArray,
        task_id: 0,
        tId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
        ...(extractedClone && {
          extractedQId:
            getSelectedItemData?.question_id || getSelectedItemData?.qId,
          extractedTId:
            getSelectedItemData?.task_id || getSelectedItemData?.tId,
        }),
      };
    }
    return getSelectedItem;
  };

  const getRandomizeDefaultValues = () => {
    return {
      block_id: 0,
      bId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
      block_name: `Randomization Block ${subNumCountingCapital[getRandomizeBlocks?.length > 0 ? getRandomizeBlocks?.length : 0]}`,
    };
  };

  const cloneSingleTaskQuestionAt = (index, itemType) => {
    if (itemType === "randomization_block") {
      let getSelectedItem = taskQuestion[index];
      if (getSelectedItem?.bId || getSelectedItem?.block_id) {
        const blockId = getSelectedItem?.bId || getSelectedItem?.block_id;
        let getSelectedRandomizeBlockItems = taskQuestion.length
          ? taskQuestion.filter(
              (tq) =>
                tq?.type !== "randomization_block" &&
                (tq?.block_id === blockId || tq?.bId === blockId),
            )
          : [];

        let newBlockSchema = getRandomizeDefaultValues();

        if (getSelectedRandomizeBlockItems?.length > 0) {
          getSelectedRandomizeBlockItems = getSelectedRandomizeBlockItems.map(
            (selectedItem) => {
              return {
                ...getCloneItemData(selectedItem),
                ...newBlockSchema,
              };
            },
          );
        } else {
          getSelectedRandomizeBlockItems.push({
            ...getSelectedItem,
            ...newBlockSchema,
          });
        }

        let newUpdatedTasks = [
          ...taskQuestion,
          ...getSelectedRandomizeBlockItems,
        ];

        newUpdatedTasks = newUpdatedTasks.map((data) => {
          return { ...data, isSelected: false };
        });

        setTaskquestion(newUpdatedTasks);

        scrollToIndex.current = taskQuestion?.length; //scroll to taskquestion items last index
      }
    } else {
      let getSelectedItem = taskQuestion.find((_data, i) => i === index);
      getSelectedItem = getCloneItemData(getSelectedItem);
      const updatedItems = [
        ...taskQuestion.slice(0, index + 1),
        getSelectedItem,
        ...taskQuestion.slice(index + 1),
      ];
      setTaskquestion(updatedItems);

      scrollToIndex.current = index + 1;
    }
  };

  const handleAddQuesTask = (type, index, isRandomizeBlock) => {
    addTaskQuestion(type, "index", index, isRandomizeBlock);
  };

  const deleteSingleTaskQuestion = (index) => {
    const deletedQues = taskQuestion[index];
    const deleteQuesId = deletedQues?.qId || deletedQues?.tId;
    let getUpdatedItems = taskQuestion.filter((_data, i) => i !== index);
    getUpdatedItems = getUpdatedItems?.length
      ? getUpdatedItems?.map((item) =>
          item?.skipLogic?.length
            ? {
                ...item,
                skipLogic: item?.skipLogic.filter(
                  (sl, _idx) => sl.skipToQuestionId !== deleteQuesId,
                ),
              }
            : item,
        )
      : getUpdatedItems;
    setTaskquestion(getUpdatedItems);
    setConfirmModalVisible(false);
  };

  // const checkQuesInSkipLogic = (data, index) => {
  //   const deletedQues = taskQuestion[index];
  //   const deleteQuesId = deletedQues?.qId || deletedQues?.tId;
  //   const skipLogicValue = data?.skipLogic.find(
  //     (slData) => slData.skipToQuestionId === deleteQuesId,
  //   );
  //   return skipLogicValue;
  // };

  const deleteSubTaskQuestion = (index, parentindex) => {
    setTaskquestion((prevProps) =>
      prevProps.map((prevProp, i) => {
        if (i === parentindex) {
          return {
            ...prevProp,
            subTaskQues: prevProp.subTaskQues.filter(
              (_data, idx) => idx !== index,
            ),
          };
        }
        return prevProp;
      }),
    );
    setConfirmModalVisible(false);
  };

  const showConfirm = (
    index,
    deleteType,
    taskQuestionId,
    nestedDelete = false,
    parentIndex = 0,
  ) => {
    setConfirmModalConfig({
      index,
      deleteType,
      taskQuestionId,
      nestedDelete,
      parentIndex,
    });
    setConfirmModalVisible(true);
  };

  const showAllDeleteConfirm = () => {
    setConfirmModalConfig({
      deleteType: "",
      errorMsg: "",
    });
    setDeleteAllconfirmModalVisible(true);
  };

  const handleDeleteRandomizationBlock = async (index, blockId) => {
    console.log("blockId", blockId);
    if (blockId === 0) {
      const deletedBlock = taskQuestion[index];
      const deleteBlockId = deletedBlock?.block_id || deletedBlock?.bId;
      let getUpdatedItems = taskQuestion.filter(
        (data, i) => data?.bId !== deleteBlockId,
      );
      setTaskquestion(getUpdatedItems);
      setConfirmModalVisible(false);
    } else {
      const result = await onDeleteTaskQuestionBlock(parseInt(blockId));
      setConfirmModalVisible(false);
      if (result.success) {
        console.log("Success:", result.message);
      } else {
        console.error("Error:", result.message);
      }
    }
  };

  const handleDeleteTQ = async () => {
    const { index, nestedDelete, parentIndex, taskQuestionId, deleteType } =
      confirmModalConfig;

    if (deleteType === "block") {
      handleDeleteRandomizationBlock(index, taskQuestionId);
      return;
    }
    // const checkQuesAddedToSkipLogic = taskQuestion.find((data, _i) => {
    //   if (checkQuesInSkipLogic(data, index)) {
    //     return data;
    //   }
    // });
    // if (checkQuesAddedToSkipLogic) {
    //   setConfirmModalConfig((prev) => ({
    //     ...prev,
    //     errorMsg:
    //       "You can't perform delete as this question/task is being used in one of the skip logic's.",
    //   }));
    //   return;
    // }

    const TQdeleteType = deleteType === "task" ? "task" : "question";
    if (nestedDelete) {
      if (taskQuestionId === 0) {
        deleteSubTaskQuestion(index, parentIndex);
      } else {
        const result = await onDeleteTaskQuestion(
          [parseInt(taskQuestionId)],
          TQdeleteType,
        );
        setConfirmModalVisible(false);
        if (result.success) {
          console.log("Success:", result.message);
        } else {
          console.error("Error:", result.message);
        }
      }
    } else {
      if (taskQuestionId === 0 || isEmptyCheck(taskQuestionId)) {
        deleteSingleTaskQuestion(index);
      } else {
        const result = await onDeleteTaskQuestion(
          [parseInt(taskQuestionId)],
          TQdeleteType,
        );
        setConfirmModalVisible(false);
        if (result.success) {
          console.log("Success:", result.message);
        } else {
          console.error("Error:", result.message);
        }
      }
    }
  };

  const handleDeleteAllTQ = async () => {
    const getSelectedTasks = taskQuestion.filter(
      (data) => data.isSelected && data.type === "task",
    );
    const getSelectedQuestions = taskQuestion.filter(
      (data) => data.isSelected && data.type === "question",
    );

    if (getSelectedTasks.length > 0) {
      const allSubTasksSelected = getSelectedTasks.every((task) =>
        task.subTaskQues.every((sub) => sub.isSubTQSelected === true),
      );

      if (!allSubTasksSelected) {
        setConfirmModalConfig((prev) => ({
          ...prev,
          errorMsg:
            "Please select all the questions added within the task to be deleted.",
        }));
        return false;
      }
    }

    let deleteAllPaylod = {
      task_id: [],
      question_id: [],
    };

    if (getSelectedQuestions.length > 0) {
      deleteAllPaylod.task_id = [
        ...deleteAllPaylod.task_id,
        ...getSelectedQuestions.map((data) => parseInt(data.question_id)),
      ];
    }

    if (getSelectedTasks.length > 0) {
      deleteAllPaylod.task_id = [
        ...deleteAllPaylod.task_id,
        ...getSelectedTasks.map((data) => parseInt(data.task_id)),
      ];
    }

    const result = await onDeleteTaskQuestion(deleteAllPaylod, "deleteAll");
    setConfirmModalVisible(false);
    if (result.success) {
      console.log("Success:", result.message);
      setDeleteAllconfirmModalVisible(false);
    } else {
      console.error("Error:", result.message);
      setDeleteAllconfirmModalVisible(false);
    }
  };

  const showQuestionChangeConfirm = (onConfirm) => {
    return confirmDialog({
      message: (
        <div className="mt-4">
          Are you sure you want to change the question type? All the applied
          Skip Logic with this question will be removed automatically.
        </div>
      ),
      header: "Confirm",
      position: "top",
      accept: () => onConfirm(true),
      reject: () => onConfirm(false),
      acceptClassName: "createClientBtn confirm-model",
      rejectClassName: "cancel",
      acceptLabel: "Yes",
      rejectLabel: "No",
    });
  };

  const getQuestionSchema = (surveyType) => {
    const quesSkeleton = JSON.parse(
      JSON.stringify(AddQuesSkeleton(surveyType)),
    );
    return {
      question_id: 0,
      tId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
      qId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
      type: "question",
      isSelected: false,
      ...quesSkeleton,
      skipLogic: [],
    };
  };

  const getTaskSchema = (surveyType) => {
    const taskSkeleton = JSON.parse(
      JSON.stringify(AddTaskSkeleton(surveyType)),
    );
    return {
      task_id: 0,
      tId: `${random32BitBinary()}_${taskQuestion.length + 1}`,
      ...taskSkeleton,
      subTaskQues: [],
      skipLogic: [],
    };
  };

  const addTaskQuestion = (
    type,
    addtype = "normal",
    index = 0,
    isRandomizeBlock = false,
  ) => {
    // getRandomize block data if item needs to be between the randomize block items
    let randomizeBlockData = {};

    if (isRandomizeBlock) {
      const getBlockData = taskQuestion[index];
      randomizeBlockData = {
        block_id: getBlockData?.block_id,
        bId: getBlockData?.bId,
        block_name: getBlockData?.block_name,
      };
    }

    if (type === "task") {
      const taskItem = { ...randomizeBlockData, ...getTaskSchema(surveyType) };
      if (addtype === "index") {
        const updatedItems = [
          ...taskQuestion.slice(0, index + 1),
          taskItem,
          ...taskQuestion.slice(index + 1),
        ];
        setTaskquestion(updatedItems);
      } else {
        setTaskquestion((prev) => [...prev, taskItem]);
      }
    } else if (type === "question") {
      const questionItem = {
        ...randomizeBlockData,
        ...getQuestionSchema(surveyType),
      };
      if (addtype === "index") {
        const updatedItems = [
          ...taskQuestion.slice(0, index + 1),
          questionItem,
          ...taskQuestion.slice(index + 1),
        ];
        setTaskquestion(updatedItems);
      } else {
        setTaskquestion((prev) => [...prev, questionItem]);
      }
    } else {
      let newBlockSchema = getRandomizeDefaultValues();

      const blockItem = {
        ...newBlockSchema,
        type: "randomization_block",
      };

      if (addtype === "index") {
        const updatedItems = [
          ...taskQuestion.slice(0, index + 1),
          blockItem,
          ...taskQuestion.slice(index + 1),
        ];
        setTaskquestion(updatedItems);
      } else {
        setTaskquestion((prev) => [...prev, blockItem]);
      }
    }
  };

  const handleCheckboxSelection = (index, itemType, checkValue) => {
    if (itemType === "task") {
      setTaskquestion((prevProps) =>
        prevProps.map((prevProp, i) => {
          if (i === index) {
            return {
              ...prevProp,
              isSelected: checkValue,
              subTaskQues: prevProp?.subTaskQues.length
                ? prevProp.subTaskQues.map((item) => ({
                    ...item,
                    isSubTQSelected: checkValue,
                  }))
                : [],
            };
          }
          return prevProp;
        }),
      );
    } else {
      setTaskquestion((prevProps) =>
        prevProps.map((prevProp, i) => {
          if (i === index) {
            return { ...prevProp, isSelected: !prevProp.isSelected };
          }
          return prevProp;
        }),
      );
    }
  };

  const updateTaskQuestionItem = (
    index,
    updateItem,
    value,
    childIndex = null,
  ) => {
    const taskQuesItem = taskQuestion[index];
    if (
      ((taskQuesItem?.type === "question" && taskQuesItem?.skipLogic?.length) ||
        (taskQuesItem?.type === "task" &&
          taskQuesItem?.subTaskQues?.length &&
          taskQuesItem?.subTaskQues[childIndex]?.skipLogic?.length)) &&
      updateItem === "selectedQuestionOption"
    ) {
      //check any skipLogic saved in db
      const taskQuesItemData =
        childIndex != null
          ? taskQuesItem?.subTaskQues[childIndex]
          : taskQuesItem;
      const checkAllNewSkipLogic = taskQuesItemData?.skipLogic?.every(
        (slData) =>
          slData?.skip_logic_id === 0 || isEmptyCheck(slData?.skip_logic_id),
      );
      if (checkAllNewSkipLogic) {
        showQuestionChangeConfirm((onConfirm) => {
          if (onConfirm) {
            // clear skip logic and update question type
            setTaskquestion((prevProps) =>
              prevProps.map((prevProp, i) => {
                if (i === index) {
                  if (childIndex != null) {
                    return {
                      ...prevProp,
                      subTaskQues: prevProp.subTaskQues.map(
                        (subItem, subIdx) => {
                          if (subIdx === childIndex) {
                            return {
                              ...subItem,
                              [updateItem]: value,
                              skipLogic: [],
                            };
                          }
                          return subItem;
                        },
                      ),
                    };
                  } else {
                    return { ...prevProp, [updateItem]: value, skipLogic: [] };
                  }
                }
                return prevProp;
              }),
            );
          } else {
            return;
          }
        });
      } else {
        showToast({
          severity: "error",
          summary: "Error",
          detail:
            "Some skip logic saved in the db. So please remove that first to change question type.",
          life: 4000,
        });
      }
    } else {
      setTaskquestion((prevProps) =>
        prevProps.map((prevProp, i) => {
          if (i === index) {
            if (childIndex != null) {
              return {
                ...prevProp,
                subTaskQues: prevProp.subTaskQues.map((subItem, subIdx) => {
                  if (subIdx === childIndex) {
                    return {
                      ...subItem,
                      [updateItem]:
                        typeof value === "object" &&
                        value !== null &&
                        !(value instanceof File)
                          ? JSON.parse(JSON.stringify(value))
                          : value,
                    };
                  }
                  return subItem;
                }),
              };
            } else {
              return {
                ...prevProp,
                [updateItem]:
                  typeof value === "object" &&
                  value !== null &&
                  !(value instanceof File)
                    ? JSON.parse(JSON.stringify(value))
                    : value,
              };
            }
          }
          return prevProp;
        }),
      );
    }
  };

  const checkAllUnChecked = () => {
    return taskQuestion.every((item) => item.isSelected === false);
  };

  // Check if shuffle tempItem ID exists in skipLogic's skipToQuestionId
  const checkTempItemInSkipLogic = (
    tempItem,
    currentIndex = null,
    isSubQuestion = false,
    parentIndex = null,
  ) => {
    const tempItemId = tempItem?.qId || tempItem?.question_id || tempItem?.tId;

    if (!tempItemId) return false;

    if (isSubQuestion && parentIndex !== null) {
      // For sub-questions, check only items in subTaskQues
      const parentItem = taskQuestion[parentIndex];
      if (parentItem?.subTaskQues?.length > 0) {
        const subQues = parentItem.subTaskQues[currentIndex];
        return (
          subQues?.skipLogic?.length > 0 &&
          subQues?.skipLogic?.some(
            (sl) => parseInt(sl.skipToQuestionId) === parseInt(tempItemId),
          )
        );
      }
      return false;
    }

    // For main items
    if (currentIndex !== null) {
      const taskQuesItem = taskQuestion[currentIndex];
      if (taskQuesItem?.skipLogic?.length > 0) {
        return taskQuesItem.skipLogic.some(
          (sl) => parseInt(sl.skipToQuestionId) === parseInt(tempItemId),
        );
      }
      if (taskQuesItem?.subTaskQues?.length > 0) {
        return taskQuesItem.subTaskQues.some((subQues) => {
          if (subQues?.skipLogic?.length > 0) {
            return subQues.skipLogic.some(
              (sl) => parseInt(sl.skipToQuestionId) === parseInt(tempItemId),
            );
          }
          return false;
        });
      }
      return false;
    }

    return false;
  };

  const handleShuffle = (direction, indexOrShuffleIndex, taskIndex = null) => {
    const taskQuestionsData = [...taskQuestion];

    if (taskIndex !== null) {
      // Sub-question shuffle
      const shuffleIndex = indexOrShuffleIndex;
      if (direction === "up") {
        if (shuffleIndex > 0) {
          const tempItem =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex];
          const shuffleTemCheckItemForUp =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex];
          const isTempItemInSkipLogic = checkTempItemInSkipLogic(
            shuffleTemCheckItemForUp,
            shuffleIndex - 1,
            true,
            taskIndex,
          );
          if (isTempItemInSkipLogic) {
            console.warn(
              "Cannot shuffle: This item is referenced in skip logic",
              shuffleTemCheckItemForUp,
            );
            showToast({
              severity: "error",
              summary: "Error",
              detail:
                "Cannot shuffle: Shuffle order item is referenced in skip logic",
              life: 4000,
            });
            return;
          }
          taskQuestionsData[taskIndex].subTaskQues[shuffleIndex] =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex - 1];
          taskQuestionsData[taskIndex].subTaskQues[shuffleIndex - 1] = tempItem;
        }
        setTaskquestion(taskQuestionsData);
      }
      if (direction === "down") {
        if (
          shuffleIndex <
          taskQuestionsData[taskIndex].subTaskQues.length - 1
        ) {
          const tempItem =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex];
          const shuffleTemCheckItemForDown =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex + 1];
          const isTempItemInSkipLogic = checkTempItemInSkipLogic(
            shuffleTemCheckItemForDown,
            shuffleIndex,
            true,
            taskIndex,
          );
          if (isTempItemInSkipLogic) {
            console.warn(
              "Cannot shuffle: This item is referenced in skip logic",
              shuffleTemCheckItemForDown,
            );
            showToast({
              severity: "error",
              summary: "Error",
              detail:
                "Cannot shuffle: Shuffle order item is referenced in skip logic",
              life: 4000,
            });
            return;
          }
          taskQuestionsData[taskIndex].subTaskQues[shuffleIndex] =
            taskQuestionsData[taskIndex].subTaskQues[shuffleIndex + 1];
          taskQuestionsData[taskIndex].subTaskQues[shuffleIndex + 1] = tempItem;
        }
        setTaskquestion(taskQuestionsData);
      }
    } else {
      // Main task/question shuffle
      const index = indexOrShuffleIndex;
      if (direction === "up") {
        if (index > 0) {
          const tempItem = taskQuestionsData[index];
          const shuffleToItem = taskQuestionsData[index - 1];
          //check if randomization block item check shuffleTo item in block or not
          if (!checkIsSameBlock(tempItem, shuffleToItem)) {
            return false;
          }

          const isTempItemInSkipLogic = checkTempItemInSkipLogic(
            tempItem,
            index - 1,
            false,
          );
          if (isTempItemInSkipLogic) {
            console.warn(
              "Cannot shuffle: This item is referenced in skip logic",
              tempItem,
            );
            showToast({
              severity: "error",
              summary: "Error",
              detail:
                "Cannot shuffle: Shuffle order item is referenced in skip logic",
              life: 4000,
            });
            return;
          }
          taskQuestionsData[index] = shuffleToItem;
          taskQuestionsData[index - 1] = tempItem;
        }
        setTaskquestion(taskQuestionsData);
      }
      if (direction === "down") {
        if (index < taskQuestionsData.length - 1) {
          const tempItem = taskQuestionsData[index];
          const shuffleToItem = taskQuestionsData[index + 1];

          //check if randomization block item check shuffleTo item in block or not
          if (!checkIsSameBlock(tempItem, shuffleToItem)) {
            return false;
          }

          const shuffleTemCheckItemForDown = shuffleToItem;
          const isTempItemInSkipLogic = checkTempItemInSkipLogic(
            shuffleTemCheckItemForDown,
            index,
            false,
          );
          if (isTempItemInSkipLogic) {
            console.warn(
              "Cannot shuffle: This item is referenced in skip logic",
              shuffleTemCheckItemForDown,
            );
            showToast({
              severity: "error",
              summary: "Error",
              detail:
                "Cannot shuffle: Shuffle order item is referenced in skip logic",
              life: 4000,
            });
            return;
          }
          taskQuestionsData[index] = shuffleToItem;
          taskQuestionsData[index + 1] = tempItem;
        }
        setTaskquestion(taskQuestionsData);
      }
    }
  };

  const areItemsConsecutive = (arr) => {
    if (arr.length <= 1) return true;

    // Sort the array first
    const sorted = [...arr].sort((a, b) => a - b);

    // Check if each next number is exactly 1 greater than the previous
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) {
        return false;
      }
    }

    return true;
  };

  const checkIsMoveUpDownDisabled = (btnType) => {
    //check randomization block item and outer item both select
    if (
      taskQuestion.some(
        (data, _i) => data.isSelected && !data?.block_id && !data?.bId,
      ) &&
      taskQuestion.some(
        (data, _i) => data.isSelected && (data?.block_id || data?.bId),
      )
    ) {
      return true;
    }

    const selectedItems = taskQuestion
      .map((data, index) => (data.isSelected === true ? index : -1))
      .filter((index) => index !== -1);

    if (!areItemsConsecutive(selectedItems)) {
      return true;
    }

    //Need to optimize code later inside randomization block check for both up/down
    if (btnType === "up") {
      if (
        taskQuestion.some(
          (data, i) =>
            i === 0 && data.isSelected && !data?.block_id && !data?.bId,
        )
      ) {
        return true;
      }
      //check block items selected
      const selectedBlockItems = getRandomizeSelectedBlockItems(taskQuestion);
      if (selectedBlockItems?.length > 0) {
        // check multiple blocks item selected
        if (selectedBlockItems?.length > 1) {
          return true;
        }
        const blockId =
          selectedBlockItems[0]?.bId || selectedBlockItems[0]?.block_id;

        const blockQuestions =
          groupedTaskQuestions?.find(
            (groupedTaskQuestion) =>
              groupedTaskQuestion?.bId === blockId ||
              groupedTaskQuestion?.block_id === blockId,
          )?.blockTaskQues || [];

        if (blockQuestions?.length) {
          if (blockQuestions.some((data, i) => i === 0 && data.isSelected)) {
            return true;
          }
        }
      }
    }

    if (btnType === "down") {
      if (
        taskQuestion.some(
          (data, i) =>
            i === taskQuestion.length - 1 &&
            data.isSelected &&
            !data?.block_id &&
            !data?.bId,
        )
      ) {
        return true;
      }

      //check block items selected
      const selectedBlockItems = getRandomizeSelectedBlockItems(taskQuestion);
      if (selectedBlockItems?.length > 0) {
        // check multiple blocks item selected
        if (selectedBlockItems?.length > 1) {
          return true;
        }
        const blockId =
          selectedBlockItems[0]?.bId || selectedBlockItems[0]?.block_id;

        const blockQuestions =
          groupedTaskQuestions?.find(
            (groupedTaskQuestion) =>
              groupedTaskQuestion?.bId === blockId ||
              groupedTaskQuestion?.block_id === blockId,
          )?.blockTaskQues || [];

        if (blockQuestions?.length) {
          if (
            blockQuestions.some(
              (data, i) => i === blockQuestions.length - 1 && data.isSelected,
            )
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Helper function to get item ID
  const getItemId = (item) => item?.qId || item?.question_id || item?.tId;

  // Helper function to check if skipLogic references any ID in the given list
  const hasSkipLogicReferencingIds = (item, targetIds) => {
    if (item?.skipLogic?.length > 0) {
      if (
        item.skipLogic.some((sl) =>
          targetIds.some(
            (id) => parseInt(sl.skipToQuestionId) === parseInt(id),
          ),
        )
      ) {
        return true;
      }
    }

    if (item?.subTaskQues?.length > 0) {
      if (
        item.subTaskQues.some((subQues) => {
          if (subQues?.skipLogic?.length > 0) {
            return subQues.skipLogic.some((sl) =>
              targetIds.some(
                (id) => parseInt(sl.skipToQuestionId) === parseInt(id),
              ),
            );
          }
          return false;
        })
      ) {
        return true;
      }
    }

    return false;
  };

  const checkMultipleMoveUpDownQuesHasSkipLogic = (
    shuffleItem,
    selectedItemsData,
    moveType,
  ) => {
    if (moveType === "down") {
      // Check if selectedItemsData items have skipLogic pointing to shuffleItem
      const shuffleItemId = getItemId(shuffleItem);
      return selectedItemsData.some((selectedItem) =>
        hasSkipLogicReferencingIds(selectedItem, [shuffleItemId]),
      );
    }

    if (moveType === "up") {
      // Vice versa: Check if shuffleItem has skipLogic pointing to any selectedItem
      const selectedItemIds = selectedItemsData.map(getItemId);
      return hasSkipLogicReferencingIds(shuffleItem, selectedItemIds);
    }

    return false;
  };

  const handleMoveUpDown = (btnType) => {
    const taskQuestionsData = [...taskQuestion];

    let selectedItems = taskQuestionsData
      .map((data, index) => (data.isSelected === true ? index : -1))
      .filter((index) => index !== -1);

    selectedItems = [...selectedItems].sort((a, b) => a - b);

    const selectedItemsData = taskQuestionsData.filter((_, index) =>
      selectedItems.includes(index),
    );

    if (btnType === "up") {
      const firstIndex = selectedItems[0];
      const lastIndex = selectedItems[selectedItems.length - 1];

      const shuffleItem = [taskQuestionsData[firstIndex - 1]];

      const checkItemsHasSkipLogic = checkMultipleMoveUpDownQuesHasSkipLogic(
        shuffleItem[0],
        selectedItemsData,
        "up",
      );
      if (checkItemsHasSkipLogic) {
        console.warn(
          "Cannot Move: Move order item is referenced in skip logic",
          shuffleItem,
        );
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Cannot Move: Move order item is referenced in skip logic",
          life: 4000,
        });
        return;
      }

      setTaskquestion([
        ...taskQuestionsData.slice(0, firstIndex - 1),
        ...selectedItemsData,
        ...shuffleItem,
        ...taskQuestionsData.slice(lastIndex + 1),
      ]);
    } else {
      const firstIndex = selectedItems[0];
      const lastIndex = selectedItems[selectedItems.length - 1];

      const shuffleItem = [taskQuestionsData[lastIndex + 1]];

      const checkItemsHasSkipLogic = checkMultipleMoveUpDownQuesHasSkipLogic(
        shuffleItem[0],
        selectedItemsData,
        "down",
      );
      if (checkItemsHasSkipLogic) {
        console.warn(
          "Cannot Move: Move order item is referenced in skip logic",
          shuffleItem,
        );
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Cannot Move: Move order item is referenced in skip logic",
          life: 4000,
        });
        return;
      }

      setTaskquestion([
        ...taskQuestionsData.slice(0, firstIndex),
        ...shuffleItem,
        ...selectedItemsData,
        ...taskQuestionsData.slice(lastIndex + 1 + 1),
      ]);
    }
  };

  const showLogicDialog = (index) => {
    // const selectedQuestion = taskQuestion[index];
    // console.log("selected", selectedQuestion);
    setIsSLDialogVisible(true);
    setSelectedTQIndex(index);
  };

  const handleImageUpload = (index, uploadType) => {
    let imgFile;

    if (uploadType === "photo") {
      imgFile = taskQuestion[index]?.imgFile;
    } else {
      imgFile = taskQuestion[index]?.videoThumbnailFile;
    }
    if (!imgFile) {
      updateTaskQuestionItem(
        index,
        uploadType === "photo" ? "imgUploadErr" : "videoThumbUploadErr",
        "Please upload a photo file",
        null,
      );
      return;
    }

    updateTaskQuestionItem(
      index,
      uploadType === "photo" ? "imgUploadErr" : "videoThumbUploadErr",
      "",
      null,
    );

    if (!ALLOWED_TYPES.includes(imgFile.type)) {
      updateTaskQuestionItem(
        index,
        uploadType === "photo" ? "imgUploadErr" : "videoThumbUploadErr",
        "Only .jpg and .png image files are allowed.",
      );
      return;
    }

    // File size validation
    if (uploadType === "photo" && imgFile.size > MAX_SIZE) {
      updateTaskQuestionItem(
        index,
        "imgUploadErr",
        "Image size must be less than 10MB.",
        null,
      );
      return;
    }

    if (uploadType === "videoThumb" && imgFile.size > MAX_THUMB_SIZE) {
      updateTaskQuestionItem(
        index,
        "videoThumbUploadErr",
        "Image size must be less than 10MB.",
        null,
      );
      return;
    }

    updateTaskQuestionItem(
      index,
      uploadType === "photo" ? "isImageUploading" : "isVideoThumbUploading",
      true,
      null,
    );

    onImageUpload(imgFile, uploadType, (err, errMsg, imageUrl) => {
      if (err) {
        updateTaskQuestionItem(
          index,
          uploadType === "photo" ? "isImageUploading" : "isVideoThumbUploading",
          false,
          null,
        );
        updateTaskQuestionItem(
          index,
          uploadType === "photo" ? "imgUploadErr" : "videoThumbUploadErr",
          errMsg,
          null,
        );
      } else {
        updateTaskQuestionItem(
          index,
          uploadType === "photo" ? "isImageUploading" : "isVideoThumbUploading",
          false,
          null,
        );
        updateTaskQuestionItem(
          index,
          uploadType === "photo" ? "isImageUploaded" : "isVideoThumbUploaded",
          true,
          null,
        );
        updateTaskQuestionItem(
          index,
          uploadType === "photo" ? "photoUrl" : "videoThumbnailUrl",
          imageUrl,
          null,
        );
      }
    });

    // // Dimension validation
    // const img = new Image();
    // img.src = URL.createObjectURL(file);

    // img.onload = () => {
    //   if (img.width !== PREFERRED_WIDTH || img.height !== PREFERRED_HEIGHT) {
    //     setImgUploadErr(
    //       "Preferred image size is 2560px × 1440px (16:9)."
    //     );
    //   }
    // };

    // img.onerror = () => {
    //   setImgUploadErr("Invalid image file.");
    //   e.target.value = "";
    // };
  };

  const handleVideoUpload = (index) => {
    const videoFile = taskQuestion[index]?.videoFile;
    if (!videoFile) {
      updateTaskQuestionItem(
        index,
        "videoUploadErr",
        "Please upload a video file",
        null,
      );
      return;
    }

    updateTaskQuestionItem(index, "videoUploadErr", "", null);

    if (!VIDEO_ALLOWED_TYPE.includes(videoFile.type)) {
      updateTaskQuestionItem(
        index,
        "videoUploadErr",
        "Only .mp4 video & Max. file size of 100MB files are allowed.",
        null,
      );
      return;
    }

    // File size validation
    if (videoFile.size > VIDEO_MAX_SIZE) {
      updateTaskQuestionItem(
        index,
        "videoUploadErr",
        "Image size must be less than 10MB.",
        null,
      );
      return;
    }

    updateTaskQuestionItem(index, "isVideoUploading", true, null);

    onVideoUpload(videoFile, (err, errMsg, imageUrl) => {
      if (err) {
        updateTaskQuestionItem(index, "isVideoUploading", false, null);
        updateTaskQuestionItem(index, "videoUploadErr", errMsg, null);
      } else {
        updateTaskQuestionItem(index, "isVideoUploading", false, null);
        updateTaskQuestionItem(index, "isVideoUploaded", true, null);
        updateTaskQuestionItem(index, "videoUrl", imageUrl, null);
      }
    });

    // // Dimension validation
    // const img = new Image();
    // img.src = URL.createObjectURL(file);

    // img.onload = () => {
    //   if (img.width !== PREFERRED_WIDTH || img.height !== PREFERRED_HEIGHT) {
    //     setImgUploadErr(
    //       "Preferred image size is 2560px × 1440px (16:9)."
    //     );
    //   }
    // };

    // img.onerror = () => {
    //   setImgUploadErr("Invalid image file.");
    //   e.target.value = "";
    // };
  };

  const handleSaveExtractionLogic = (
    selectedExtractedOptions,
    isExtractionQuestion,
  ) => {
    if (isExtractionQuestion) {
      setTaskquestion((prevProps) =>
        prevProps.map((prevProp, i) => {
          if (i === selectedTQIndex) {
            return {
              ...prevProp,
              selectedQuestionOption:
                selectedExtractedOptions?.selectedTargetQuesType ||
                selectedExtractedOptions?.selectedQuestionOption,
              selectedExtractedAnsOpt:
                selectedExtractedOptions?.selectedExtractedAnsOpt || "",
              selectedTargetQuesType:
                selectedExtractedOptions?.selectedTargetQuesType || "",
            };
          }
          return prevProp;
        }),
      );
    } else {
      let getSelectedItem = taskQuestion.find(
        (_data, i) => i === selectedTQIndex,
      );
      getSelectedItem = getCloneItemData(
        getSelectedItem,
        true,
        selectedExtractedOptions,
      );
      const updatedItems = [
        ...taskQuestion.slice(0, selectedTQIndex + 1),
        getSelectedItem,
        ...taskQuestion.slice(selectedTQIndex + 1),
      ];
      setTaskquestion(updatedItems);
      scrollToIndex.current = selectedTQIndex + 1;
    }

    setIsSLDialogVisible(false);
    setSelectedTQIndex(null);
  };

  return (
    <div
      className="text-start px-3 py-3"
      style={{
        // marginTop: "140px",
        background: "#fff",
      }}
    >
      <div className="section-header d-flex align-items-center justify-content-between">
        <p className="heading text_dark fw-600 fs-18">Task & Questions</p>
        {groupedTaskQuestions.length ? (
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center">
                <i className="pi pi-angle-up"></i>
                <Button
                  className="move-up-btn fw-bold"
                  aria-label="move up"
                  disabled={
                    checkAllUnChecked() ||
                    checkIsMoveUpDownDisabled("up") ||
                    isLocked
                  }
                  link
                  onClick={() => handleMoveUpDown("up")}
                >
                  Move up
                </Button>
              </div>
              <span>|</span>
              <div className="d-flex align-items-center">
                <i className="pi pi-angle-down ps-3"></i>
                <Button
                  className="move-up-btn fw-bold"
                  aria-label="move down"
                  disabled={
                    checkAllUnChecked() ||
                    checkIsMoveUpDownDisabled("down") ||
                    isLocked
                  }
                  link
                  onClick={() => handleMoveUpDown("down")}
                >
                  Move down
                </Button>
              </div>
            </div>
            <Button
              className="add-task-btn me-1 createClientBtn py-1"
              aria-label="Clone"
              onClick={cloneTaskQuestions}
              disabled={checkAllUnChecked() || isLocked}
            >
              Clone
            </Button>
            <Button
              className="add-question-btn ms-2 createClientBtn py-1"
              aria-label="Delete"
              onClick={showAllDeleteConfirm}
              disabled={checkAllUnChecked() || isLocked}
            >
              Delete
            </Button>
          </div>
        ) : (
          ""
        )}
      </div>
      {groupedTaskQuestions.length ? (
        <div className="tasks-questions-section my-4">
          {groupedTaskQuestions.map((taskQues, gIndx) => {
            const index = taskQues?.itemIndex;
            return (
              <div
                className={`mt-2 ${taskQues?.isExtraction ? "px-2" : ""}`}
                key={gIndx}
                ref={(el) =>
                  taskQues.type === "randomization_block"
                    ? ""
                    : (taskQuesRefs.current[index] = el)
                }
                style={{
                  backgroundColor: taskQues?.isExtraction ? "#f9f9ed" : "",
                }}
              >
                <ShowTQItemHeading
                  taskQues={taskQues}
                  index={index}
                  handleCheckboxSelection={handleCheckboxSelection}
                  isLocked={isLocked}
                  cloneSingleTaskQuestionAt={cloneSingleTaskQuestionAt}
                  showLogicDialog={showLogicDialog}
                  showConfirm={showConfirm}
                  taskQuestion={taskQuestion}
                  isRandomizeBlock={false}
                  subIndex={0}
                />
                {taskQues.type === "task" ? (
                  <XmotTask
                    taskQuestion={taskQues}
                    taskQuestions={taskQuestion}
                    index={index}
                    updateTaskItem={(updateType, value) =>
                      updateTaskQuestionItem(index, updateType, value, null)
                    }
                    updateTaskItemWithChildIndex={(
                      updateType,
                      value,
                      childIndex,
                    ) =>
                      updateTaskQuestionItem(
                        index,
                        updateType,
                        value,
                        childIndex,
                      )
                    }
                    handleAddQuesTask={(itemType) =>
                      handleAddQuesTask(itemType, index, false)
                    }
                    handleImageUpload={() => handleImageUpload(index, "photo")}
                    handleVideoUpload={() => handleVideoUpload(index)}
                    handleVideoThumbUpload={() =>
                      handleImageUpload(index, "videoThumb")
                    }
                    deleteSubTQ={showConfirm}
                    setTaskquestion={setTaskquestion}
                    handleShuffle={handleShuffle}
                    isLocked={isLocked}
                    surveyType={surveyType}
                    onDeleteSkipLogic={onDeleteSkipLogic}
                    deleteSlLoading={deleteTQLoading}
                  />
                ) : (
                  ""
                )}
                {taskQues.type === "question" ? (
                  <XmotQuestion
                    taskQuestion={taskQues}
                    taskQuestions={taskQuestion}
                    index={index}
                    updateQuestionItem={(updateType, value) =>
                      updateTaskQuestionItem(index, updateType, value)
                    }
                    handleAddQuesTask={(itemType) =>
                      handleAddQuesTask(itemType, index, false)
                    }
                    answerOptionText={`${index + 1}`}
                    handleShuffle={handleShuffle}
                    isLocked={isLocked}
                    surveyType={surveyType}
                  />
                ) : (
                  ""
                )}
                {taskQues.type === "randomization_block" ? (
                  <XmotRandomizationBlock
                    taskQuestion={taskQues}
                    taskQuestions={taskQuestion}
                    index={index}
                    updateTaskQuestionItem={updateTaskQuestionItem}
                    handleAddQuesTask={handleAddQuesTask}
                    answerOptionText={`${index + 1}`}
                    handleShuffle={handleShuffle}
                    isLocked={isLocked}
                    surveyType={surveyType}
                    setTaskquestion={setTaskquestion}
                    handleCheckboxSelection={handleCheckboxSelection}
                    showLogicDialog={showLogicDialog}
                    cloneSingleTaskQuestionAt={cloneSingleTaskQuestionAt}
                    handleImageUpload={handleImageUpload}
                    handleVideoUpload={handleVideoUpload}
                    onDeleteSkipLogic={onDeleteSkipLogic}
                    deleteSlLoading={deleteTQLoading}
                    showConfirm={showConfirm}
                    taskQuesRefs={taskQuesRefs}
                    addTQInRandomBlock={(itemType) => {
                      const addSchema =
                        itemType === "task"
                          ? getTaskSchema(surveyType)
                          : getQuestionSchema(surveyType);

                      if (
                        !taskQues?.blockTaskQues ||
                        taskQues?.blockTaskQues?.length === 0
                      ) {
                        setTaskquestion((prevProps) =>
                          prevProps.map((prevProp, i) => {
                            if (i === index) {
                              return {
                                ...prevProp,
                                ...addSchema,
                              };
                            }
                            return prevProp;
                          }),
                        );
                      } else {
                        const questionItem = {
                          block_id: taskQues?.block_id,
                          bId: taskQues?.bId,
                          block_name: taskQues?.block_name,
                          ...addSchema,
                        };
                        const updatedItems = [
                          ...taskQuestion.slice(
                            0,
                            index + (taskQues?.blockTaskQues?.length - 1) + 1,
                          ),
                          questionItem,
                          ...taskQuestion.slice(
                            index + (taskQues?.blockTaskQues?.length - 1) + 1,
                          ),
                        ];
                        setTaskquestion(updatedItems);
                      }
                    }}
                  />
                ) : (
                  ""
                )}
                <Divider />
              </div>
            );
          })}
        </div>
      ) : (
        ""
      )}

      <div className="text-center">
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5">
          <Button
            className="add-task-btn me-1 createClientBtn py-1"
            aria-label="Add task"
            onClick={() => addTaskQuestion("task")}
            disabled={isLocked}
          >
            Add task
          </Button>
          <Button
            className="add-question-btn ms-2 createClientBtn py-1"
            aria-label="Add question"
            onClick={() => addTaskQuestion("question")}
            disabled={isLocked}
          >
            Add question
          </Button>
          <Button
            className="add-question-btn ms-2 createClientBtn py-1"
            aria-label="Randomization Block"
            onClick={() => addTaskQuestion("randomization_block")}
            disabled={isLocked}
          >
            Randomization Block
          </Button>
        </div>
        {/* <p
          className="bottom-text mt-2 mb-0 fw-bold fs-14 pb-3"
          style={{ color: "#9E9E9E", fontStyle: "italic" }}
        >
          Note: Skip logic with questions will work only with edit mode after
          you have added all the tasks/questions and saved
        </p> */}
      </div>
      <Divider />

      {confirmModalVisible ? (
        <ConfirmationModal
          showConfirmModal={confirmModalVisible}
          setShowConfirmModal={setConfirmModalVisible}
          onDeleteClick={handleDeleteTQ}
          confirmMessage={`Are you sure you want to delete this ${confirmModalConfig.deleteType} permanently?`}
          errorMsg={confirmModalConfig.errorMsg}
          nestedDelete={confirmModalConfig.nestedDelete}
          deleteTQLoading={deleteTQLoading}
        />
      ) : (
        ""
      )}

      {deleteAllconfirmModalVisible ? (
        <ConfirmationModal
          showConfirmModal={deleteAllconfirmModalVisible}
          setShowConfirmModal={setDeleteAllconfirmModalVisible}
          onDeleteClick={handleDeleteAllTQ}
          confirmMessage={`Are you sure you want to delete all the selected task(s)/question(s) permanently?`}
          errorMsg={confirmModalConfig.errorMsg}
          deleteTQLoading={deleteTQLoading}
        />
      ) : (
        ""
      )}

      {isSLDialogVisible ? (
        <LogicDialog
          isSLDialogVisible={isSLDialogVisible}
          setIsSLDialogVisible={setIsSLDialogVisible}
          taskQuestions={taskQuestion}
          setTaskquestion={setTaskquestion}
          setSelectedTQIndex={setSelectedTQIndex}
          selectedTQIndex={selectedTQIndex}
          onDeleteSkipLogic={onDeleteSkipLogic}
          deleteSlLoading={deleteTQLoading}
          handleSaveExtractionLogic={handleSaveExtractionLogic}
        />
      ) : (
        ""
      )}
      <ConfirmDialog style={{ width: "40vw" }} />
    </div>
  );
};

export default XmotTaskQuestion;
